import { useEffect, useRef } from 'react';

/**
 * Triggers `window.print()` once after PDF content is ready to print.
 *
 * Waits for `document.fonts.ready` (when the Font Loading API is available)
 * so webfonts used in the template are loaded, then schedules
 * `window.print()` on the next animation frame so layout/paint can settle.
 * Avoids a fixed timeout that may fire too early on slow connections or too
 * late on fast ones. Falls back to printing immediately when `document.fonts`
 * doesn't exist (e.g. jsdom, or browsers without the Font Loading API).
 */
export function useAutoPrint({ isReady }: { isReady: boolean }): void {
  const hasPrinted = useRef(false);

  useEffect(() => {
    if (!isReady || hasPrinted.current) {
      return undefined;
    }
    hasPrinted.current = true;

    let cancelled = false;

    const schedulePrint = () => {
      if (cancelled) {
        return;
      }
      requestAnimationFrame(() => {
        if (!cancelled) {
          window.print();
        }
      });
    };

    if (typeof document.fonts !== 'undefined') {
      void document.fonts.ready.then(schedulePrint).catch(schedulePrint);
    } else {
      schedulePrint();
    }

    return () => {
      cancelled = true;
    };
  }, [isReady]);
}
